import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import { GIButton } from 'app-components';

import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { useManageDrugs, exportToCSV } from '../hooks/useManageDrugs';
import PaginatedTable, { renderCellContent } from '../../../../components/common/table/PaginatedTable';

const StyledConatiner = styled.div`
    padding: 15px;
    border: 1px solid ${({ theme }) => theme.gray8};
    border-radius: 6px;
    margin-bottom: 15px;
    .warningText {
        color: ${({ theme }) => theme.dangerMain};
    }
    .resultContainer {
        padding: 0px;
        margin: 0px;
        min-height: 350px;
        border: 1px solid ${({ theme }) => theme.gray8};
    }
`;

const manageDrugsColumns = [
    {
        key: 'rxcui',
        label: <FormattedMessage id='manage.drugs.grid.col.rxcui' />,
        render: (item: any) => renderCellContent(item?.rxcui),
    },
    {
        key: 'name',
        label: <FormattedMessage id='manage.drugs.grid.col.name' />,
        render: (item: any) => renderCellContent(item?.name),
    },
    {
        key: 'strength',
        label: <FormattedMessage id='manage.drugs.grid.col.strength' />,
        render: (item: any) => renderCellContent(item?.strength),
    },
    {
        key: 'route',
        label: <FormattedMessage id='manage.drugs.grid.col.route' />,
        render: (item: any) => renderCellContent(item?.route),
    },
    {
        key: 'fullName',
        label: <FormattedMessage id='manage.drugs.grid.col.fullName' />,
        render: (item: any) => renderCellContent(item?.fullName),
    },
    {
        key: 'genericRxcui',
        label: <FormattedMessage id='manage.drugs.grid.col.genericRxcui' />,
        render: (item: any) => renderCellContent(item?.genericRxcui),
    },
    {
        key: 'status',
        label: <FormattedMessage id='manage.drugs.grid.col.status' />,
        render: (item: any) => renderCellContent(item?.status),
    }
];

const SearchDrugs = ({ setTotalRXCUI }) => {

    const { drugList, inputDrugs, handleDrugIdsChange, invalidId, isInvalidLength, resetInputDrugList, handleSearchClick } = useManageDrugs();
    const [isRxcuiDisplayWarning, setisRxcuiDisplayWarning] = useState(false);
    const [hiddenColumn, setHiddenColumn] = useState(new Set());
    const [resetColumn, setResetColumn] = useState(0);
    const isSearchDisabled = !!invalidId || !inputDrugs || isInvalidLength;
    const isExportDisabled = !!invalidId || !inputDrugs || drugList.length === 0;

    // Function to trigger reset of child state
    const handleResetChild = () => {
        setResetColumn((prev) => prev + 1); // Toggling to trigger reset
    };

    useEffect(() => {
        setTotalRXCUI(drugList.length);
    }, [drugList]);

    useEffect(() => {
        if (inputDrugs.length > 0 && !isRxcuiDisplayWarning) {
            setisRxcuiDisplayWarning(true);
        }
    }, [inputDrugs]);

    const resetList = () => {
        handleResetChild();
        setHiddenColumn(new Set());
        resetInputDrugList();
    };

    return (
        <StyledConatiner>
            <h3 className="padding-2" data-testid="search-drug-header">
                <FormattedMessage id='manage.drugs.header.rxcui' />
            </h3>
            <div className="padding-2 padding-top-10">
                <div className="padding-bottom-2" data-testid="enter-drug-id-label">
                    <FormattedMessage id='manage.drugs.enter.rxcui' />
                    {' '}<span className='warningText' >*</span>
                    <br />
                </div>
                <textarea
                    data-testid="drug-input-text"
                    name="rxcuiFilter" id="rxcuiFilter"
                    rows={3}
                    value={inputDrugs}
                    className="col-7"
                    onChange={handleDrugIdsChange} />
                <br />
                <ConditionalRendering isTrue={!inputDrugs && isRxcuiDisplayWarning}>
                    <div className='warningText' data-testid="warning-rxcui-required">
                        <FormattedMessage id='manage.drugs.error.required.rxcui' />
                    </div>
                </ConditionalRendering>
                <ConditionalRendering isTrue={!!invalidId}>
                    <FormattedMessage id='manage.drugs.error.invalid.rxcui' data-testid="warning-invalid-id-message" />
                    <span className='warningText' data-testid="warning-invalid-input">{invalidId}</span>
                    <br />
                </ConditionalRendering>
                <ConditionalRendering isTrue={isInvalidLength}>
                    <span className='warningText' data-testid="warning-exceed-input">
                        <FormattedMessage id='manage.drugs.error.exceeds.rxcui' />
                    </span>
                    <br />
                </ConditionalRendering>
                <div className="padding-top-2 padding-bottom-2">
                    <GIButton type='button' id='search-btn' labelId={'button.search'} isDisabled={isSearchDisabled} onClickHandler={() => handleSearchClick()} />
                    <GIButton type='button' variant='secondary' id='export-btn' labelId={'button.export'} isDisabled={isExportDisabled} onClickHandler={() => exportToCSV(drugList, hiddenColumn)} />
                    <GIButton type='button' variant='secondary' id='reset-btn' labelId={'button.reset'} onClickHandler={resetList} />
                </div>
                <ConditionalRendering isTrue={!drugList.length}>
                    <div className="watermark">
                        <FormattedMessage id='manage.drugs.error.nodata' />
                    </div>
                </ConditionalRendering>
                <div className='resultContainer' data-testid="drug-list-table">
                    <PaginatedTable columns={manageDrugsColumns} data={drugList} pageSize={20} hidePageControl={true} gethidenColumnList={setHiddenColumn} resetColumn={resetColumn} />
                </div>
            </div>
        </StyledConatiner>
    );
};

export default SearchDrugs;